// Mock `contacts` source — one row per contact, each assigned to a chain
// (opportunity). Contacts and deals are SIBLINGS under a shared chain; they never
// link directly. A deals×contacts report joins them on the shared chain key
// (a deal's opportunity_id == a contact's opportunity_id), exactly like the
// deal×activity join — one row per matched pair. That fanout means additive
// measures (sum revenue) double-count within a chain; use `distinct`/per-row or
// count(distinct deal_id). See docs/reporting-prototype-contacts-source.md.
import { Faker, en } from '@faker-js/faker'
import { OPPORTUNITY_IDS } from './mock-opportunity'

/** One contact, hanging off a chain (opportunity). No direct link to any deal. */
export type ContactRow = {
  contact_id: number
  name: string
  title: string // groupby dim
  department: string // groupby dim
  opportunity_id: number // the shared chain key — the deals join matches on this
}

// Isolated faker instance, seeded per generate() call → deterministic regardless
// of module import order. A shared global faker would interleave RNG draws across
// the mock files and break repeatability; an own instance keeps this source's
// stream private.
const faker = new Faker({ locale: [en] })

// Foodservice-relevant title/department pools — faker's generic jobTitle/department
// output reads off-domain for a food CRM, so we curate these and let faker drive
// the names (its strength) and act as the picker RNG.
const TITLES = [
  'VP Food Service',
  'Director of Dining',
  'Procurement Manager',
  'Executive Chef',
  'Category Buyer',
  'Operations Lead',
]
const DEPARTMENTS = [
  'Culinary',
  'Procurement',
  'Operations',
  'Finance',
  'Supply Chain',
]

export function generateContacts(count: number, seed: number): ContactRow[] {
  faker.seed(seed)
  const rows: ContactRow[] = []
  for (let i = 0; i < count; i++) {
    rows.push({
      contact_id: 500_000 + i,
      name: faker.person.fullName(),
      title: faker.helpers.arrayElement(TITLES),
      department: faker.helpers.arrayElement(DEPARTMENTS),
      // Uniform across chains → ~20 contacts per chain (300 / 15).
      opportunity_id: faker.helpers.arrayElement(OPPORTUNITY_IDS),
    })
  }
  return rows
}

/** Canonical contacts dataset. Fixed seed → repeatable demos. */
export const MOCK_CONTACTS: ContactRow[] = generateContacts(300, 7)

/** Join index: chain (opportunity_id) → its contacts. Built once at import. */
export const contactsByChain: ReadonlyMap<number, ContactRow[]> = (() => {
  const m = new Map<number, ContactRow[]>()
  for (const c of MOCK_CONTACTS) {
    if (!m.has(c.opportunity_id)) m.set(c.opportunity_id, [])
    m.get(c.opportunity_id)!.push(c)
  }
  return m
})()
